import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import type { HabitWithStreakState } from "@shared/schema";
import CreateHabitModal from "@/components/create-habit-modal";
import HabitCard from "@/components/habit-card";

interface GoalHabitsTabProps {
  goalId: string;
}

export default function GoalHabitsTab({ goalId }: GoalHabitsTabProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { data: habits = [], isLoading } = useQuery<HabitWithStreakState[]>({
    queryKey: ["/api/habits", goalId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (goalId) params.append("goalId", goalId);
      const response = await fetch(`/api/habits?${params.toString()}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error('Failed to fetch habits');
      return response.json();
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Habits</h2>
          <p className="text-sm text-muted-foreground">Habits linked to this goal - complete them directly here</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)} data-testid="button-create-habit">
          <Plus className="w-4 h-4 mr-2" />
          New Habit
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8">
          <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading habits...</p>
        </div>
      ) : habits.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">No habits linked to this goal yet</p>
            <Button onClick={() => setIsCreateModalOpen(true)} variant="outline" data-testid="button-create-first-habit">
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Habit
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {habits.map((habit) => (
            <HabitCard 
              key={habit.id} 
              habit={habit} 
              viewMode="list"
            />
          ))}
        </div>
      )}

      <CreateHabitModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        goalId={goalId}
      />
    </div>
  );
}
